import { businessDateOf } from '../../../../utils/dateUtils';
import dayjs, { type Dayjs } from 'dayjs';
import type { OrderDto } from '../../dto';
import type { OrderDeliveryMode } from '../../../transports/transportDirection';
import type { ProductVariant } from '../../../../types/pz';
import {
  asQuantityUnit,
  buildQuantitySetFromPair,
  convertCommercialQuantity,
  getCommercialPairMismatch,
  getFractionalPieces,
  getQuantitySetValue,
  hasPersistedQuantitySet,
  isAllocationUnit,
  quantitySetForEnteredAmount,
  quantitySetFromFields,
  quantitySetToFields,
  roundCommercialQuantity,
  scaleQuantitySetToAmount,
} from '../../../../utils/commercialQuantity';
import { getVariantOrderLinePatch } from '../../../products/orderVariantSelection';
import { getLineTotal, getTotalsByCurrency, type OrderProductLineDraft } from '../../orderProductLineTotals';
import { omitUnusedOrderProductHeight } from '../../orderProductDimensions';
import { toOrderProductPayload } from '../../orderProductPayload';
import { buildCreateOrderPayload } from '../../api/ordersApi';
import { getDefaultCustomerPaymentDueDate } from '../../customerPayment';
import {
  DEFAULT_PREPAYMENT_PERCENT,
  normalizeOrderPaymentMode,
  normalizePrepaymentPercent,
  type OrderPaymentMode,
} from '../../orderPaymentMode';
import type { OrderProductPayload } from '../../api/orderProductsApi';
import { CUSTOMER_ORDER_NUMBER_PATTERN, MAX_CUSTOMER_ORDER_NUMBER_LENGTH } from '../../../../dto/order';

/**
 * The Szkic step of the ZK card (makieta `sSzkic`, owner 2026-10-05): one
 * editable model for a new order (before the first save) and for a saved
 * order in status Szkic. Pure functions only — the hook
 * `useOrderDraftEditor` owns the state and the commands, the screen
 * `OrderDraftEditor` renders it. Save rules stay where they were: the create
 * payload is `buildCreateOrderPayload`, a line is `toOrderProductPayload`
 * (same as „Szczegóły → Sprzedaż”), the header goes through `PUT /orders/:id`.
 */

export interface OrderDraftLine extends OrderProductLineDraft {
  /** Stable row key; equals `id` for a saved line. */
  key: string;
  /** Saved `OrderProduct.id`; absent for a line added on the card. */
  id?: string;
  unit: string;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  /**
   * The last positive quantity, kept while the field is cleared so the price amount and
   * the conversion keep their proportion when a new quantity is typed. UI state only:
   * never part of a payload or of the „changed” comparison.
   */
  lastQuantity?: number;
  /** Catalog dimensions profile — decides only whether height is used. */
  productCategoryDimensionDisplayProfile?: string | null;
}

/**
 * „Dane szkicu” and „Więcej danych”. Fields of the retired create wizard
 * stay here (decision 2 of the task): numbering, customer order number,
 * account manager, delivery mode, dates, payment, supplier/loading place,
 * notes, offer flag and extra notification recipients.
 */
export interface OrderDraftHeader {
  customerId: string | null;
  deliveryLocation: string;
  orderDate: Dayjs | null;
  useManualNumbering: boolean;
  manualOrderNumber: string;
  clientOrderNumber: string;
  accountManagerId: string | null;
  deliveryMode: OrderDeliveryMode;
  expectedDelivery: Dayjs | null;
  customerPaymentDueDate: Dayjs | null;
  customerPaymentNotes: string;
  /** „Płatność” (decision 2026-10-27); the percent only matters for a prepayment. */
  paymentMode: OrderPaymentMode;
  prepaymentPercent: number | null;
  supplierId: string | null;
  factoryLocation: string;
  notes: string;
  /** Create only (the order update has no such field). */
  notifyUsers: string[];
  /** Currency of a newly added line (the order currency). */
  currency: string;
}

export interface OrderDraftState {
  header: OrderDraftHeader;
  lines: OrderDraftLine[];
}

export type OrderDraftHeaderField = keyof OrderDraftHeader;

let lineSequence = 0;
const newLineKey = () => {
  lineSequence += 1;
  return `new-${Date.now()}-${lineSequence}`;
};

const toDay = (value: string | null | undefined): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

export function createEmptyOrderDraft(currency = 'PLN', now: Dayjs = dayjs()): OrderDraftState {
  return {
    header: {
      customerId: null,
      deliveryLocation: '',
      orderDate: now,
      useManualNumbering: false,
      manualOrderNumber: '',
      clientOrderNumber: '',
      accountManagerId: null,
      deliveryMode: 'OUR_DELIVERY',
      expectedDelivery: null,
      customerPaymentDueDate: getDefaultCustomerPaymentDueDate(now),
      customerPaymentNotes: '',
      paymentMode: 'TERM',
      prepaymentPercent: null,
      supplierId: null,
      factoryLocation: '',
      notes: '',
      notifyUsers: [],
      currency: currency || 'PLN',
    },
    lines: [],
  };
}

export function orderDraftFromOrder(order: OrderDto): OrderDraftState {
  const currency = order.currency || 'PLN';
  return {
    header: {
      customerId: order.customerId || null,
      deliveryLocation: order.deliveryLocation || '',
      orderDate: toDay(businessDateOf(order.orderDate)),
      useManualNumbering: Boolean(order.useManualNumbering),
      manualOrderNumber: order.manualOrderNumber || '',
      clientOrderNumber: order.clientOrderNumber || '',
      accountManagerId: order.accountManagerId || null,
      deliveryMode: order.deliveryMode === 'CUSTOMER_PICKUP' ? 'CUSTOMER_PICKUP' : 'OUR_DELIVERY',
      expectedDelivery: toDay(order.expectedDelivery),
      customerPaymentDueDate: toDay(order.customerPaymentDueDate),
      customerPaymentNotes: order.customerPaymentNotes || '',
      paymentMode: normalizeOrderPaymentMode(order.paymentMode),
      prepaymentPercent: order.paymentMode === 'PREPAYMENT' ? normalizePrepaymentPercent(order.prepaymentPercent) : null,
      supplierId: order.supplierId || null,
      factoryLocation: order.factoryLocation || '',
      notes: order.notes || '',
      notifyUsers: [],
      currency,
    },
    lines: (order.productsLoaded ? order.products : []).map((product) => ({
      ...product,
      key: product.id,
      id: product.id,
      unit: product.unit || 'pcs',
      currency: product.currency || currency,
    })),
  };
}

/**
 * A new line from the catalog pick of „Dodaj produkt”: the variant patch
 * (name snapshot, dimensions, weight, customer or catalog price) plus the
 * entered amount, in one unit for quantity and price (szt./m²/m³).
 */
export function buildOrderDraftLine(input: {
  variantId: string;
  variant: ProductVariant | undefined;
  quantity: number;
  unit: string;
  unitPrice: number | null;
  currency: string;
}): OrderDraftLine {
  const unit = asQuantityUnit(input.unit);
  const patch = omitUnusedOrderProductHeight(getVariantOrderLinePatch(
    input.variantId,
    input.variant,
    { unit, currency: input.currency },
  ));
  const quantity = roundCommercialQuantity(input.quantity);
  return {
    ...patch,
    key: newLineKey(),
    unit,
    quantity,
    saleUnit: unit,
    saleQuantity: quantity,
    conversionUnit: null,
    // The price entered in „Dodaj produkt” wins over the customer/catalog price of the patch.
    unitPrice: input.unitPrice ?? patch.unitPrice ?? 0,
    currency: patch.currency || input.currency,
  };
}

/**
 * Inline „Ilość” of the table: the physical amount in the line unit. The
 * price amount (and the optional conversion view) follow it — converted from
 * the line's own dimensions, exactly as the backend validates the pair
 * (80,092 m³ → 1618,02 szt., not the saved ratio's 1618 szt.); only a line whose
 * dimensions cannot convert keeps the previous proportion. The same amount
 * typed again keeps the saved pair and set (same rule as the unit-conversion editor).
 */
export function setOrderDraftLineQuantity(line: OrderDraftLine, quantity: number | null): OrderDraftLine {
  const current = Number(line.quantity);
  if (quantity === null || !Number.isFinite(quantity) || quantity <= 0) {
    // Cleared: the billing amounts stay as they were, the last positive quantity is remembered.
    return { ...line, quantity: null, lastQuantity: current > 0 ? current : line.lastQuantity };
  }
  const next = roundCommercialQuantity(quantity);
  const previous = current > 0 ? current : Number(line.lastQuantity);
  // The same amount: nothing to re-express (a saved rounded pair stays as it was).
  if (Number.isFinite(previous) && roundCommercialQuantity(previous) === next) {
    return { ...line, lastQuantity: undefined, quantity: next };
  }
  const unit = asQuantityUnit(line.unit);
  const saleUnit = asQuantityUnit(line.saleUnit || line.unit);
  const ratio = Number.isFinite(previous) && previous > 0 ? next / previous : null;
  const dimensions = {
    width: line.width ?? undefined,
    length: line.length ?? undefined,
    height: line.height ?? undefined,
    thickness: line.thickness ?? undefined,
    countInPiecesOnly: line.countInPiecesOnly === true,
  };
  const convert = (target: string | null | undefined, current: number | string | null | undefined) => {
    if (!target || target === unit) return next;
    if (isAllocationUnit(unit) && isAllocationUnit(target)) {
      const fromDimensions = convertCommercialQuantity(next, unit, target, dimensions);
      if (!fromDimensions.error && fromDimensions.value) return fromDimensions.value;
    }
    if (ratio !== null && Number.isFinite(Number(current))) return roundCommercialQuantity(Number(current) * ratio);
    return undefined;
  };
  const updated: OrderDraftLine = {
    ...line,
    lastQuantity: undefined,
    quantity: next,
    saleQuantity: convert(saleUnit, line.saleQuantity) ?? next,
    ...(line.conversionUnit
      ? { conversionQuantity: convert(line.conversionUnit, line.conversionQuantity) }
      : {}),
  };
  if (hasPersistedQuantitySet(line as unknown as Record<string, unknown>) && isAllocationUnit(unit)) {
    const set = quantitySetFromFields(line as unknown as Record<string, unknown>);
    const snapshot = quantitySetForEnteredAmount(set, next, unit, dimensions);
    const nextSet = snapshot
      ? scaleQuantitySetToAmount(snapshot, next, unit)
      : buildQuantitySetFromPair({
        physicalQuantity: next,
        physicalUnit: unit,
        pricingQuantity: Number(updated.saleQuantity),
        pricingUnit: saleUnit,
        ...(line.conversionUnit && isAllocationUnit(line.conversionUnit) && updated.conversionQuantity != null
          ? { conversionQuantity: Number(updated.conversionQuantity), conversionUnit: line.conversionUnit }
          : {}),
      }, dimensions);
    if (nextSet) return { ...updated, ...quantitySetToFields(nextSet) };
  }
  return updated;
}

/**
 * A line whose amount, price amount or conversion was changed on the card and no
 * longer agrees with the line's dimensions (the backend would refuse the save with
 * ORDER_PRODUCT_QUANTITY_PAIR_INCONSISTENT). The table marks it before saving.
 * A saved pair left unchanged is not judged here. A „Liczymy tylko w sztukach”
 * line is not judged either: the draft hides its converter, so the message
 * would point at a control that is not there.
 */
export function hasOrderDraftLinePairMismatch(line: OrderDraftLine, savedLine?: OrderDraftLine | null): boolean {
  if (line.countInPiecesOnly === true) return false;
  if (!(Number(line.quantity) > 0) || !isAllocationUnit(line.unit)) return false;
  const pairOf = (row: OrderDraftLine) => [row.quantity, row.unit, row.saleQuantity, row.saleUnit || row.unit, row.conversionQuantity, row.conversionUnit]
    .map((value) => (value == null ? '' : String(value))).join('|');
  if (savedLine && pairOf(savedLine) === pairOf(line)) return false;
  const conversionUnit = line.conversionUnit && isAllocationUnit(line.conversionUnit) ? line.conversionUnit : null;
  return getCommercialPairMismatch({
    physicalQuantity: Number(line.quantity),
    physicalUnit: line.unit,
    pricingQuantity: Number(line.saleQuantity ?? line.quantity),
    pricingUnit: asQuantityUnit(line.saleUnit || line.unit),
    ...(conversionUnit && line.conversionQuantity != null ? { conversionQuantity: Number(line.conversionQuantity), conversionUnit } : {}),
  }, {
    width: line.width ?? undefined,
    length: line.length ?? undefined,
    height: line.height ?? undefined,
    thickness: line.thickness ?? undefined,
    countInPiecesOnly: false,
  }) !== null;
}

/**
 * Whole pieces only (owner 2026-10-06): a line in m²/m³ whose amount was changed on
 * the card and is not a whole number of szt. — the table marks it and the save is
 * refused until it is rounded in „Zmień przelicznik”. A saved line left unchanged
 * stays saveable as it is; szt. lines and „tylko w sztukach” are never judged.
 */
export function hasOrderDraftLineFractionalPieces(line: OrderDraftLine, savedLine?: OrderDraftLine | null): boolean {
  if (!(Number(line.quantity) > 0) || !isAllocationUnit(line.unit)) return false;
  const pairOf = (row: OrderDraftLine) => [row.quantity, row.unit, row.saleQuantity, row.saleUnit || row.unit, row.conversionQuantity, row.conversionUnit]
    .map((value) => (value == null ? '' : String(value))).join('|');
  if (savedLine && pairOf(savedLine) === pairOf(line)) return false;
  const saleUnit = line.saleUnit || line.unit;
  const explicitPieces = saleUnit === 'pcs' && line.saleQuantity != null
    ? line.saleQuantity
    : (line.conversionUnit === 'pcs' && line.conversionQuantity != null ? line.conversionQuantity : null);
  return getFractionalPieces(line.quantity, line.unit, {
    width: line.width ?? undefined,
    length: line.length ?? undefined,
    height: line.height ?? undefined,
    thickness: line.thickness ?? undefined,
    countInPiecesOnly: line.countInPiecesOnly === true,
  }, explicitPieces) !== null;
}

export function isOrderDraftLineQuantityValid(line: OrderDraftLine): boolean {
  return Number(line.quantity) > 0;
}

export function isOrderDraftLinePriceValid(line: OrderDraftLine): boolean {
  return Number(line.unitPrice) > 0;
}

/**
 * „Stwórz ofertę” needs at least one line and every line with a quantity
 * and — for readers of sale prices — a sale price (makieta `bar()` step 1).
 * Without the price capability the price is unknown, never „missing”.
 */
export function getOrderDraftReadiness(lines: readonly OrderDraftLine[], canViewSalePrices: boolean) {
  const incomplete = lines.filter((line) => !isOrderDraftLineQuantityValid(line)
    || (canViewSalePrices && !isOrderDraftLinePriceValid(line))).length;
  return {
    lineCount: lines.length,
    linesWithoutPrice: incomplete,
    ready: lines.length > 0 && incomplete === 0,
  };
}

export const getOrderDraftLineValue = (line: OrderDraftLine) => getLineTotal(line);

export const getOrderDraftTotals = (lines: readonly OrderDraftLine[], fallbackCurrency: string) =>
  getTotalsByCurrency(lines, fallbackCurrency);

/**
 * The „Dodaj produkt” row of the editor as the save sees it: `line` is a
 * complete row not yet added to the table (saving adds it), `incomplete` a
 * chosen product still missing its quantity or price.
 */
export interface OrderDraftPendingRow {
  line: OrderDraftLine | null;
  incomplete: boolean;
}

/**
 * Key of the save-error toast of the Szkic (a validation problem or a failed save):
 * one copy at a time, it closes itself, on the next attempt, on the next edit,
 * on a step change and when the screen goes away.
 */
export const ORDER_DRAFT_SAVE_ERROR_KEY = 'zk-card-save-error';
/** Sticky warning handed from the new-order screen to its card (`deliveryModeNotSaved`): the card closes it on a step change or when it is left. */
export const ORDER_DRAFT_HANDOVER_WARNING_KEY = 'zk-card-handover-warning';

/** The draft as saved: a complete, not yet added „Dodaj produkt” row becomes the last line. */
export function applyOrderDraftPendingRow(draft: OrderDraftState, pending: OrderDraftPendingRow | null): OrderDraftState {
  const line = pending?.line;
  // Not twice when this row is already in the table; another line of the same product is fine.
  if (!line || draft.lines.some((existing) => existing.key === line.key)) {
    return draft;
  }
  return { ...draft, lines: [...draft.lines, line] };
}

/** A prepayment share is empty (saved as 100 %) or 0 < x <= 100; no antd clamp coerces a typed value. */
export const isOrderDraftPrepaymentPercentValid = (header: Pick<OrderDraftHeader, 'paymentMode' | 'prepaymentPercent'>) => (
  header.paymentMode !== 'PREPAYMENT' || header.prepaymentPercent == null
  || (header.prepaymentPercent > 0 && header.prepaymentPercent <= 100)
);

/**
 * Why the draft cannot be saved yet (an `orders:` key), or null — the first
 * missing condition only, so one save raises one message.
 */
export function getOrderDraftSaveProblem(
  draft: OrderDraftState,
  pending?: OrderDraftPendingRow | null,
  savedLines: readonly OrderDraftLine[] = [],
): string | null {
  if (!draft.header.customerId) return 'orders:process.draft.errors.customer';
  if (pending?.incomplete) return 'orders:process.draft.errors.addRowIncomplete';
  if (draft.lines.length === 0) return 'orders:process.draft.errors.noLines';
  if (draft.lines.some((line) => !isOrderDraftLineQuantityValid(line))) return 'orders:process.draft.errors.quantity';
  if (draft.lines.some((line) => hasOrderDraftLineFractionalPieces(line, savedLines.find((saved) => saved.key === line.key)))) {
    return 'orders:process.draft.errors.fractionalPieces';
  }
  if (!isOrderDraftPrepaymentPercentValid(draft.header)) return 'orders:process.draft.errors.prepaymentPercent';
  if (draft.header.useManualNumbering) {
    const number = draft.header.manualOrderNumber.trim();
    if (!number) return 'orders:modal.validation.manualNumberRequired';
    if (number.length > MAX_CUSTOMER_ORDER_NUMBER_LENGTH || !CUSTOMER_ORDER_NUMBER_PATTERN.test(number)) {
      return 'orders:process.draft.errors.manualNumber';
    }
  }
  return null;
}

const ISO = (value: Dayjs | null) => (value && value.isValid() ? value.toISOString() : undefined);
const text = (value: string) => value.trim() || null;

export function buildOrderDraftProductsPayload(
  lines: readonly OrderDraftLine[],
  options: { includeSalePrices: boolean; fallbackCurrency: string },
): OrderProductPayload[] {
  return lines.map((line) => toOrderProductPayload({ ...line, id: line.id }, options));
}

/**
 * `POST /orders` body for the first save. The roles that never see a field
 * do not send it (same as the retired wizard): the account manager without
 * `canEditAccountManager`, payment terms without `canViewCustomerPayments`.
 * `deliveryMode` is not part of the create contract — a self-pickup draft is
 * saved by the update right after (`needsDeliveryModeUpdate`).
 */
export function buildOrderDraftCreatePayload(
  draft: OrderDraftState,
  can: { includeSalePrices: boolean; editAccountManager: boolean; viewCustomerPayments: boolean },
): Record<string, unknown> {
  const { header } = draft;
  const values: Record<string, unknown> = {
    customerId: header.customerId,
    currency: header.currency,
    orderDate: header.orderDate && header.orderDate.isValid() ? header.orderDate : undefined,
    useManualNumbering: header.useManualNumbering,
    manualOrderNumber: header.useManualNumbering ? header.manualOrderNumber.trim() : undefined,
    clientOrderNumber: text(header.clientOrderNumber),
    deliveryLocation: text(header.deliveryLocation),
    supplierId: header.supplierId || null,
    factoryLocation: text(header.factoryLocation),
    expectedDelivery: header.expectedDelivery && header.expectedDelivery.isValid() ? header.expectedDelivery : undefined,
    notes: header.notes.trim() || undefined,
    notifyUsers: header.notifyUsers,
    ...paymentModePayload(header),
    ...(can.editAccountManager && header.accountManagerId ? { accountManagerId: header.accountManagerId } : {}),
    ...(can.viewCustomerPayments ? {
      customerPaymentDueDate: header.customerPaymentDueDate && header.customerPaymentDueDate.isValid()
        ? header.customerPaymentDueDate : undefined,
      customerPaymentNotes: header.customerPaymentNotes,
    } : {}),
    products: buildOrderDraftProductsPayload(draft.lines, {
      includeSalePrices: can.includeSalePrices,
      fallbackCurrency: header.currency,
    }),
  };
  return buildCreateOrderPayload(values as Parameters<typeof buildCreateOrderPayload>[0], header.currency, {
    includeProductSalePrices: can.includeSalePrices,
  });
}

/** `paymentMode` + `prepaymentPercent` (only for a prepayment; another mode clears it on the server). */
function paymentModePayload(header: OrderDraftHeader): Record<string, unknown> {
  return header.paymentMode === 'PREPAYMENT'
    ? { paymentMode: header.paymentMode, prepaymentPercent: header.prepaymentPercent ?? DEFAULT_PREPAYMENT_PERCENT }
    : { paymentMode: header.paymentMode };
}

export const needsDeliveryModeUpdate = (draft: OrderDraftState) => draft.header.deliveryMode === 'CUSTOMER_PICKUP';

/** Header fields the order update accepts, with the role that may change them (backend `orderPermissions.js`). */
export interface OrderDraftHeaderEditRights {
  orderDate?: boolean;
  commercial: boolean;
  accountManager: boolean;
  customerPayments: boolean;
}

/**
 * `PUT /orders/:id` body with only the header fields that changed — a field
 * the role may not change is never sent unchanged (the backend refuses a
 * body with a restricted field). Customer, offer flag and notification
 * recipients are not part of the update contract.
 */
export function buildOrderDraftHeaderUpdate(
  saved: OrderDraftHeader,
  edited: OrderDraftHeader,
  rights: OrderDraftHeaderEditRights,
): Record<string, unknown> {
  const update: Record<string, unknown> = {};
  const changed = (field: OrderDraftHeaderField) => JSON.stringify(saved[field]) !== JSON.stringify(edited[field]);
  const dateValue = (value: Dayjs | null) => ISO(value) ?? null;
  if (changed('clientOrderNumber')) update.clientOrderNumber = text(edited.clientOrderNumber);
  if (changed('expectedDelivery')) update.expectedDelivery = dateValue(edited.expectedDelivery);
  if (changed('notes')) update.notes = edited.notes;
  if (rights.orderDate && changed('orderDate') && edited.orderDate) update.orderDate = edited.orderDate.format('YYYY-MM-DD');
  if (rights.commercial) {
    if (changed('deliveryLocation')) update.deliveryLocation = text(edited.deliveryLocation);
    if (changed('useManualNumbering') || changed('manualOrderNumber')) {
      update.useManualNumbering = edited.useManualNumbering;
      update.manualOrderNumber = edited.useManualNumbering ? edited.manualOrderNumber.trim() || null : null;
    }
    if (changed('supplierId')) update.supplierId = edited.supplierId || null;
    if (changed('factoryLocation')) update.factoryLocation = text(edited.factoryLocation);
    if (changed('deliveryMode')) update.deliveryMode = edited.deliveryMode;
    if (changed('paymentMode') || changed('prepaymentPercent')) Object.assign(update, paymentModePayload(edited));
  }
  if (rights.accountManager && changed('accountManagerId')) update.accountManagerId = edited.accountManagerId || null;
  if (rights.customerPayments) {
    if (changed('customerPaymentDueDate')) update.customerPaymentDueDate = dateValue(edited.customerPaymentDueDate);
    if (changed('customerPaymentNotes')) update.customerPaymentNotes = edited.customerPaymentNotes.trim() || null;
  }
  return update;
}

/** What a line saves, without its key and UI-only state: two lines with the same signature save the same. */
export const orderDraftLineSignature = ({ key: _key, lastQuantity: _last, ...line }: OrderDraftLine) => JSON.stringify(line);

const linesSignature = (lines: readonly OrderDraftLine[]) => lines.map(orderDraftLineSignature).join('\n');

export const haveOrderDraftLinesChanged = (saved: readonly OrderDraftLine[], edited: readonly OrderDraftLine[]) =>
  linesSignature(saved) !== linesSignature(edited);

export const isOrderDraftDirty = (saved: OrderDraftState, edited: OrderDraftState) =>
  JSON.stringify(saved.header) !== JSON.stringify(edited.header) || haveOrderDraftLinesChanged(saved.lines, edited.lines);

/**
 * „Ściąga logistyczna” of one line: what the product has today (dimensions,
 * thickness, piece weight — no pallets since 2026-09-30) and the amounts the
 * quantity set gives (szt., m², m³, weight = szt. × waga sztuki).
 */
export interface OrderDraftLogistics {
  pieces: number | null;
  m2: number | null;
  m3: number | null;
  weightKg: number | null;
}

export function getOrderDraftLineLogistics(line: OrderDraftLine): OrderDraftLogistics {
  const quantity = Number(line.quantity);
  const unit = asQuantityUnit(line.unit);
  const empty = { pieces: null, m2: null, m3: null, weightKg: null };
  if (!(quantity > 0) || !isAllocationUnit(unit)) return empty;
  const set = hasPersistedQuantitySet(line as unknown as Record<string, unknown>)
    ? quantitySetFromFields(line as unknown as Record<string, unknown>)
    : buildQuantitySetFromPair({
      physicalQuantity: quantity,
      physicalUnit: unit,
      pricingQuantity: Number(line.saleQuantity ?? quantity),
      pricingUnit: asQuantityUnit(line.saleUnit || unit),
      conversionQuantity: line.conversionQuantity,
      conversionUnit: line.conversionUnit ?? null,
    }, {
      width: line.width ?? undefined,
      length: line.length ?? undefined,
      height: line.height ?? undefined,
      thickness: line.thickness ?? undefined,
      countInPiecesOnly: line.countInPiecesOnly === true,
    });
  if (!set) return empty;
  const pieces = getQuantitySetValue(set, 'pcs');
  const pieceWeight = Number(line.grossWeight);
  return {
    pieces,
    m2: getQuantitySetValue(set, 'm2'),
    m3: getQuantitySetValue(set, 'm3'),
    weightKg: pieces !== null && pieceWeight > 0 ? roundCommercialQuantity(pieces * pieceWeight) : null,
  };
}

/** Sum of the order; a total is shown only when every line has that amount. */
export function getOrderDraftLogisticsTotals(lines: readonly OrderDraftLine[]): OrderDraftLogistics {
  const each = lines.map(getOrderDraftLineLogistics);
  const sum = (field: keyof OrderDraftLogistics) => (each.length > 0 && each.every((item) => item[field] !== null)
    ? roundCommercialQuantity(each.reduce((total, item) => total + Number(item[field]), 0))
    : null);
  return { pieces: sum('pieces'), m2: sum('m2'), m3: sum('m3'), weightKg: sum('weightKg') };
}
